import React, { useMemo } from 'react';
import {
  ICohortRow,
  IRowMeta,
  setCohortRowDatePicker,
  setCohortRowDateRange,
  setCohortRowDurationStartDate,
} from '../../../../../redux/slices/oldBuilder';
import { IDateInput } from '../../../../../redux/slices/oldBuilder/date/types';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import * as BuilderUI from '../../../BuilderUI';
import KubitTooltip from '../../../../KubitTooltip';
import { DatePicker } from '../../../../DatePicker';
import { isNil } from 'lodash';
import { MaybeNil } from '../../../../../utils/interfaces';
import {
  activeSchemaIdSelector,
  builderDateConfigSelector,
  errorPrimitiveSelector,
} from '../../../../../redux/selectors/builder';
import { globalFormat, isDateValid } from '../../../../../utils/formatting';
import { INTERNAL_DATE_FORMAT } from '../../../../../config/constants';

import { builderDateSelector } from '../../../../../redux/selectors/date';
import { toJavaScriptDate } from '../../../DatePickerWidget/utils';
import OptionalDatePickerWidget from '../../../DatePickerWidget/OptionalDatePickerWidget';
import { IDateRange } from '../../../../../utils/apollo/graphqlSchema';

interface IProps {
  rowMeta: IRowMeta;
  cohortRow: ICohortRow;
}

export default ({ rowMeta, cohortRow }: IProps) => {
  const dispatch = useAppDispatch();
  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const cohortDurationStartError = useAppSelector((state) =>
    Boolean(errorSelector(state, rowMeta, 'cohortDurationStart')),
  );
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const builderDateInput = useAppSelector(builderDateSelector);
  const dateConfig = useAppSelector(builderDateConfigSelector);

  const { datePicker, cohortDurationStart, contextInput } = cohortRow;
  const { dateRange } = contextInput;

  const handleSetDatePicker = (newDateInput: MaybeNil<IDateInput>) => {
    dispatch(
      setCohortRowDatePicker({
        rowMeta,
        dateInput: newDateInput,
      }),
    );
  };

  const handleSetDateRange = (newDateRange: MaybeNil<IDateRange>) => {
    dispatch(
      setCohortRowDateRange({
        rowMeta,
        dateRange: newDateRange,
      }),
    );
  };

  const handleCohortDurationStartDateChange = (date: MaybeNil<Date>) => {
    const durationStartDate =
      !isNil(date) && isDateValid(date)
        ? globalFormat.formatDate(date, INTERNAL_DATE_FORMAT)
        : null;
    dispatch(
      setCohortRowDurationStartDate({
        rowMeta,
        durationStartDate,
      }),
    );
  };

  return (
    <>
      <BuilderUI.BuilderFieldContainer>
        <KubitTooltip
          content="Select date range for cohort analysis"
          placement="bottom"
          noOpenDelay={true}
          disabled={!isNil(dateRange)}
        >
          <OptionalDatePickerWidget
            endDate={dateConfig.maxDate}
            onChange={handleSetDatePicker}
            onUpdateDateRange={handleSetDateRange}
            datePicker={datePicker}
            dateRange={dateRange}
            defaultDatePicker={
              builderDateInput.datePicker ?? {
                __typename: 'DatePickerFixed',
                start: builderDateInput.startDate,
                end: builderDateInput.endDate,
              }
            }
            schemaId={activeSchemaId}
          />
        </KubitTooltip>
      </BuilderUI.BuilderFieldContainer>
      {!isNil(datePicker) && cohortDurationStart.required && (
        <BuilderUI.BuilderFieldContainer>
          <BuilderUI.BuilderFieldLabel>Since</BuilderUI.BuilderFieldLabel>
          <DatePicker
            placeholderText={'Cohort duration start'}
            onChange={handleCohortDurationStartDateChange}
            selectedValue={
              !isNil(cohortDurationStart.date)
                ? toJavaScriptDate(cohortDurationStart.date)
                : null
            }
            maxDate={
              !isNil(dateRange) ? toJavaScriptDate(dateRange.start) : null
            }
            hasError={cohortDurationStartError}
          />
        </BuilderUI.BuilderFieldContainer>
      )}
    </>
  );
};
